import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Loads the offer-fbt-cards section via the Shopify Product Recommendations
 * API and injects the rendered HTML. Re-fetches on cart updates.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} fbtContainer - Container where recommendation HTML is injected.
 *
 * @extends {Component<Refs>}
 */
class OfferFbtLoaderComponent extends Component {
  static observedAttributes = ['data-product-id'];

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {boolean} */
  #connected = false;

  /** @type {boolean} */
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();
    this.#connected = true;
    this.#load();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#connected = false;
    this.#abortController?.abort();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Reloads when the product-id attribute changes.
   * @param {string} name
   * @param {string|null} oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-product-id' && oldValue !== newValue && this.#connected) {
      this.#load();
    }
  }

  /**
   * Fetches the offer-fbt-cards section via the recommendations API
   * and injects the returned HTML.
   */
  async #load() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId || 'offer-fbt-cards';
    const limit = this.dataset.limit || '4';
    const intent = this.dataset.intent || 'related';

    if (!productId) {
      this.hidden = true;
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}recommendations/products?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=${intent}`;

      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const content = doc.querySelector('.offer-fbt-cards');

      const container = this.refs.fbtContainer
        || this.querySelector('[ref="fbtContainer"]');

      if (container && content) {
        container.innerHTML = content.outerHTML;

        // Inject stylesheet blocks from the fetched section (if not already present)
        doc.querySelectorAll('style').forEach((srcStyle) => {
          if (!document.querySelector(`style[data-fbt-style]`) && srcStyle.textContent.includes('offer-fbt-cards')) {
            const style = document.createElement('style');
            style.setAttribute('data-fbt-style', '');
            style.textContent = srcStyle.textContent;
            document.head.appendChild(style);
          }
        });

        // Execute inline module scripts from the fetched section
        doc.querySelectorAll('script[type="module"]').forEach((srcScript) => {
          const script = document.createElement('script');
          script.type = 'module';
          script.textContent = srcScript.textContent;
          container.appendChild(script);
        });

        this.#loaded = true;
        this.hidden = false;
      } else if (!content) {
        this.hidden = true;
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to load FBT recommendations:', error);
      }
    }
  }

  /** Reloads recommendations on cart change. */
  #handleCartUpdate = () => {
    if (this.#connected) this.#load();
  };
}

if (!customElements.get('offer-fbt-loader-component')) {
  customElements.define('offer-fbt-loader-component', OfferFbtLoaderComponent);
}
